import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import * as services from "./../../services/saleng";
import AppBreadcrumbs from "../../components/AppBreadcrumbs";
import moment from "moment";
import "moment/locale/th";

// Icons
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import DescriptionIcon from "@mui/icons-material/Description";
import PersonIcon from "@mui/icons-material/Person";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

export default function SOListPage() {
  const navigator = useNavigate();
  const [search, setSearch] = useState("");
  const [type, setType] = useState("open");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [rowsData, setRowsData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Debounce search
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const getSOs = async () => {
    setLoading(true);
    try {
      const res = await services.getSaleOrders();
      const resData = res.data.data;
      
      const mapped = resData.map((so) => ({
        id: so.id.toString(),
        // soNumber: (so.soNumber && so.soNumber.split("-").pop()) || "-", // Assuming format similar to PO if needed, but preserved logic uses ID mainly or simple fields
        description: so.detail || "-",
        name: so.customer?.name || "-",
        status: so.status,
        itemsCount: so.items?.length ?? 0,
        startDateVal: so.startDate,
        endDateVal: so.endDate,
        startDateDisplay: moment(so.startDate).add(543, "year").format("D MMM YY"),
        endDateDisplay: moment(so.endDate).add(543, "year").format("D MMM YY"),
      }));

      setRowsData(mapped);
    } catch (err) {
      console.error("❌ โหลด SO ล้มเหลว", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getSOs();
  }, []);

  const getMinEndDate = (start) =>
    new Date(new Date(start).setDate(new Date(start).getDate() + 1))
      .toISOString()
      .split("T")[0];

  const filteredRows = useMemo(() => {
    return rowsData.filter((row) => {
      const s = debouncedSearch.toLowerCase();
      const matchSearch =
        s === "" ||
        row.description.toLowerCase().includes(s) ||
        row.name.toLowerCase().includes(s) ||
        row.id.includes(s);
        
      const matchStatus = type === "all" ? true : row.status === type;

      const matchStart = !startDate || row.startDateVal >= startDate;
      const matchEnd = !endDate || row.endDateVal <= endDate;

      return matchSearch && matchStatus && matchStart && matchEnd;
    });
  }, [rowsData, debouncedSearch, type, startDate, endDate]);

  const getStatusBadge = (status) => {
    const map = {
      open: { label: "เปิด", classes: "bg-green-100 text-green-700 border-green-200" },
      close: { label: "สิ้นสุด", classes: "bg-red-50 text-red-600 border-red-100" },
      cancel: { label: "ยกเลิก", classes: "bg-gray-100 text-gray-600 border-gray-200" },
    };
    const { label, classes } = map[status] || { label: "Unkown", classes: "bg-gray-100 text-gray-600" };
    return (
      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${classes}`}>
        {label}
      </span>
    );
  };

  const handleEdit = (id) => {
    navigator(`so_edit?id=${id}`);
  };

  return (
    <div className="min-h-[calc(100vh-64px)] bg-gray-50 p-4 md:p-8">
      <AppBreadcrumbs />

      <div className="max-w-7xl mx-auto w-full">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center my-6 gap-4">
          <div>
             <h2 className="text-2xl font-bold text-gray-800">ใบเสนอราคาขาย (SO)</h2>
             <p className="text-gray-500 text-sm mt-1">จัดการรายการใบเสนอราคาขายทั้งหมด</p>
          </div>
          <button
            onClick={() => navigator("so_create")}
            className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium px-5 py-2.5 rounded-xl shadow-sm transition-all hover:shadow-md active:translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <AddIcon />
            <span>เปิดใบเสนอราคา</span>
          </button>
        </div>

        {/* Controls Section */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-200 mb-6 flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <SearchIcon className="text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="ค้นหาเลขที่, ชื่อลูกค้า, หรือรายละเอียด..."
              className="bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-xl focus:ring-green-500 focus:border-green-500 block w-full pl-10 pr-10 p-2.5 outline-none transition-all"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              >
                <ClearIcon fontSize="small" />
              </button>
            )}
          </div>

          <div className="w-px bg-gray-200 hidden lg:block mx-2"></div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4 overflow-x-auto pb-1 sm:pb-0">
             {/* Status Buttons */}
             <div className="flex bg-gray-100/50 p-1 rounded-xl border border-gray-200 w-max">
                {[
                  { id: 'open', label: 'เปิด' },
                  { id: 'close', label: 'สิ้นสุด' },
                  { id: 'cancel', label: 'ยกเลิก' },
                ].map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setType(t.id)}
                    className={`px-4 py-2 rounded-lg text-xs font-medium transition-all ${
                      type === t.id
                        ? "bg-white text-gray-800 shadow-sm border border-gray-100"
                        : "text-gray-500 hover:text-gray-700 hover:bg-gray-200/50"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
            </div>

            {/* Date Range */}
            <div className="flex gap-2 items-center">
               <div className="relative">
                 <input
                   type="date"
                   className="bg-white border border-gray-200 text-gray-700 text-sm rounded-xl focus:ring-green-500 focus:border-green-500 block w-full p-2.5 outline-none"
                   value={startDate || ""}
                   onChange={(e) => {
                     setStartDate(e.target.value);
                     setEndDate("");
                   }}
                 />
               </div>
               <span className="text-gray-400">-</span>
               <div className="relative">
                 <input
                   type="date"
                   className="bg-white border border-gray-200 text-gray-700 text-sm rounded-xl focus:ring-green-500 focus:border-green-500 block w-full p-2.5 outline-none"
                   value={endDate || ""}
                   min={startDate ? getMinEndDate(startDate) : undefined}
                   onChange={(e) => setEndDate(e.target.value)}
                 />
               </div>
            </div>
          </div>
        </div>

        {/* Content Table */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
          {loading ? (
             <div className="p-8 text-center text-gray-500">
                 กำลังโหลดข้อมูล...
             </div>
          ) : filteredRows.length === 0 ? (
            <div className="text-center py-20 border-gray-300">
              <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                 <DescriptionIcon className="text-gray-400" style={{ fontSize: 32 }} />
              </div>
              <h3 className="text-xl font-bold text-gray-600 mb-1">ไม่พบรายการ SO</h3>
              <p className="text-gray-500">ลองปรับตัวกรองหรือสร้างรายการใหม่</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50/50 border-b border-gray-200 text-xs text-gray-500 uppercase tracking-wider">
                    <th className="px-6 py-4 font-semibold">SO #</th>
                    <th className="px-6 py-4 font-semibold w-1/4">รายละเอียด</th>
                    <th className="px-6 py-4 font-semibold">ลูกค้า</th>
                    <th className="px-6 py-4 font-semibold text-center">สถานะ</th>
                    <th className="px-6 py-4 font-semibold text-center">สินค้า</th>
                    <th className="px-6 py-4 font-semibold whitespace-nowrap">วันที่เริ่ม</th>
                    <th className="px-6 py-4 font-semibold whitespace-nowrap">วันที่สิ้นสุด</th>
                    <th className="px-4 py-4 font-semibold text-right"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredRows.map((row) => (
                    <tr
                      key={row.id}
                      onClick={() => handleEdit(row.id)}
                      className="hover:bg-gray-50/80 transition-colors cursor-pointer group"
                    >
                      <td className="px-6 py-4">
                         <span className="font-bold text-gray-800 font-mono text-sm">#{row.id.padStart(4, "0")}</span>
                      </td>
                      <td className="px-6 py-4">
                         <div className="max-w-[300px]">
                            <p className="text-sm text-gray-700 line-clamp-2" title={row.description}>
                               {row.description}
                            </p>
                         </div>
                      </td>
                      <td className="px-6 py-4">
                         <div className="flex items-center gap-2">
                            <PersonIcon className="text-gray-400" style={{ fontSize: 18 }} />
                            <span className="text-sm text-gray-700 font-medium" title={row.name}>{row.name}</span>
                         </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                         {getStatusBadge(row.status)}
                      </td>
                      <td className="px-6 py-4 text-center">
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-gray-100 text-gray-600 text-xs font-medium">
                             <Inventory2Icon className="text-green-600" style={{ fontSize: 14 }} />
                             {row.itemsCount}
                          </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                         <div className="flex items-center gap-1.5 text-sm text-gray-600">
                            <CalendarTodayIcon style={{ fontSize: 14 }} className="text-gray-400" />
                            <span>{row.startDateDisplay}</span>
                         </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                         <div className="flex items-center gap-1.5 text-sm text-gray-600">
                            <CalendarTodayIcon style={{ fontSize: 14 }} className="text-gray-400" />
                            <span>{row.endDateDisplay}</span>
                         </div>
                      </td>
                      <td className="px-4 py-4 text-right">
                         <ArrowForwardIcon className="text-gray-300 group-hover:text-green-500 transition-colors" fontSize="small" /> 
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
